import React, { memo, useCallback } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import "../styles/ServiceCard.css";

/* =========================================================
   🧩 Motion Variants (Smooth & Minimal Luxury Transitions)
========================================================= */
const CARD_INITIAL = { opacity: 0, y: 26 };
const CARD_ANIMATE = { opacity: 1, y: 0 };
const CARD_TRANSITION = { duration: 0.45, ease: "easeOut" };

const CARD_HOVER = { scale: 1.045 };
const CARD_TAP = { scale: 0.97 };

const BTN_HOVER = { scale: 1.05 };
const BTN_TAP = { scale: 0.88 };

/* =========================================================
   🛡️ Safe Text Conversion
========================================================= */
function safeText(v) {
  if (typeof v === "string") return v;
  if (v == null) return "";
  try {
    return String(v);
  } catch {
    return "";
  }
}

/* =========================================================
   🎨 ServiceCard Component
========================================================= */
function ServiceCard({
  title,
  description,
  image,
  onBook,
  isSelected,
  disabled,
  className,
  highlightLabel,
}) {
  const safeTitle = safeText(title);
  const safeDesc = safeText(description);

  /* 🔐 Prevent outer card click from triggering Book */
  const handleBookClick = useCallback(
    (e) => {
      e?.stopPropagation?.();
      if (!disabled && typeof onBook === "function") onBook();
    },
    [onBook, disabled]
  );

  const computedClass = [
    "service-card",
    isSelected ? "selected-service" : "",
    disabled ? "service-card-disabled" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <motion.article
      className={computedClass}
      initial={CARD_INITIAL}
      animate={CARD_ANIMATE}
      whileHover={!disabled ? CARD_HOVER : {}}
      whileTap={!disabled ? CARD_TAP : {}}
      transition={CARD_TRANSITION}
      role="button"
      data-selected={isSelected}
      data-disabled={disabled}
    >
      {/* 📌 Service Image */}
      {Boolean(image) && (
        <motion.img
          src={image}
          alt={safeTitle}
          loading="lazy"
          className="service-card-img"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
        />
      )}

      {/* 📌 Body */}
      <div className="service-card-body">
        <div className="service-card-header-row">
          <h3 className="service-card-title">{safeTitle}</h3>
          {Boolean(isSelected) && (
            <span className="service-card-chip">{highlightLabel || "Selected"}</span>
          )}
        </div>
        <p className="service-card-desc">{safeDesc}</p>
      </div>

      {/* 📌 Footer / Book Button */}
      <motion.div
        className="card-footer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <motion.button
          type="button"
          className="btn service-card-btn press3d"
          aria-label={`Book ${safeTitle}`}
          onClick={handleBookClick}
          whileHover={!disabled ? BTN_HOVER : {}}
          whileTap={!disabled ? BTN_TAP : {}}
          disabled={disabled}
        >
          {disabled ? "Unavailable" : "Book"}
        </motion.button>
      </motion.div>
    </motion.article>
  );
}

/* =========================================================
   📌 PropTypes & Defaults
========================================================= */
ServiceCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  image: PropTypes.string,
  onBook: PropTypes.func.isRequired,
  isSelected: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  highlightLabel: PropTypes.string,
};

ServiceCard.defaultProps = {
  description: "",
  image: "",
  isSelected: false,
  disabled: false,
  className: "",
  highlightLabel: "Selected",
};

export default memo(ServiceCard);
